import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError } from 'rxjs/operators';
import { environment } from "src/environments/environment";
import { Ato } from "../model/ato.model";
import { ApiErrorHandlerService } from "./api-error-handler.service";
import { BuscaAto } from "../model/busca-ato.model";
import { ArquivoBinario } from "../model/arquivo-binario.model";
import { VisaoAto } from "../model/visao-ato.model";
import { VISAO_ANOTACAO, Visao } from "../model/visao.model";
import { VisaoRelacional } from "../model/visao-relacional.model";
import { Retorno } from "../model/retorno.model";
import { AtoConsulta } from "../model/ato-consulta.model";
import { Orgao } from "../model/orgao.model";
import { ComparacaoAto } from "../model/comparacao-ato.model";
import { DataUtil } from "../util/data.util";
import { TipoConsulta } from "../model/tipo-consulta.model";

@Injectable({
    providedIn: 'root'
})
export class AtoService {

    private baseUrl = `${environment.apiUrl}/ato`;
    private baseUrlAnexo = `${environment.apiUrl}/anexo`;
    private baseUrlConsultaExterna = `${environment.apiUrl}/consulta-externa/ato`;
    private baseUrlConsultaInterna = `${environment.apiUrl}/consulta-interna/ato`;

    constructor(
        private http: HttpClient,
        private errorHandler: ApiErrorHandlerService
    ) { }

    findById(id: number): Observable<Ato> {
        const url = `${this.baseUrl}/${id}`;
        return this.http.get<Ato>(url).pipe(catchError(this.errorHandler.handle()));
    }

    buscarPorIdSemSegmentos(id: number): Observable<Ato> {
        const url = `${this.baseUrl}/${id}/sem-segmentos`;
        return this.http.get<Ato>(url).pipe(catchError(this.errorHandler.handle()));
    }

    buscarPorFiltro(filtro: BuscaAto): Observable<AtoConsulta[]> {
        const url = `${this.baseUrl}/buscarPorFiltro`;
        return this.http.post<AtoConsulta>(url, filtro).pipe(catchError(this.errorHandler.handle()));
    }

    buscarPorEpigrafe(ato: Ato, orgaosSelecionados: Orgao[]): Observable<AtoConsulta[]> {

        const filtro = new BuscaAto();

        filtro.dataPublicacaoStr = undefined;
        filtro.listaIdsTipoAto = [];
        filtro.listaIdsTipoAto.push(ato.tipoAto.idTipoAto);
        filtro.numeroAto = ato.numeroAto;
        filtro.dataAtoInicialStr = DataUtil.getDataStr(ato.dataAto);
        filtro.dataAtoFinalStr = DataUtil.getDataStr(ato.dataAto);
        filtro.listaSiglasOrgao = [];
        orgaosSelecionados.forEach(o => filtro.listaSiglasOrgao?.push(o.siglaOrgao));
        filtro.exibirEmenta = true;
        filtro.apenasComTodosOrgaos = true;

        return this.buscarPorFiltro(filtro);
    }

    create(ato: Ato): Observable<string> {
        const url = `${this.baseUrl}/create`;

        const formData = new FormData();
        formData.append('ato', new Blob([JSON.stringify(ato)], { type: "application/json" }));

        return this.http.post<string>(url, formData).pipe(catchError(this.errorHandler.handle()));
    }

    corrigirAto(ato: Ato): Observable<string> {
        const url = `${this.baseUrl}/corrigir`;

        const formData = new FormData();
        formData.append('ato', new Blob([JSON.stringify(ato)], { type: "application/json" }));

        return this.http.post<string>(url, formData).pipe(catchError(this.errorHandler.handle()));
    }

    republicarAto(idAtoOriginalRepublicacao : number | undefined, ato: Ato): Observable<string> {
        const url = `${this.baseUrl}/${idAtoOriginalRepublicacao}/republicar`;

        const formData = new FormData();
        formData.append('ato', new Blob([JSON.stringify(ato)], { type: "application/json" }));

        return this.http.post<string>(url, formData).pipe(catchError(this.errorHandler.handle()));
    }

    excluirRepublicacaoAto(idAto: number | undefined): Observable<Retorno> {
        const url = `${this.baseUrl}/${idAto}/excluir-republicacao`;
        return this.http.delete<number>(url).pipe(catchError(this.errorHandler.handle()));
    }

    buscarDetalheParaEdicao(id: number): Observable<Ato> {
        const url = `${this.baseUrl}/detalhe/${id}`;
        return this.http.get<Ato>(url).pipe(catchError(this.errorHandler.handle()));
    }

    uploadAnexo(file: File): Observable<ArquivoBinario> {
        const url = `${this.baseUrlAnexo}/`;
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<number>(url, formData).pipe(catchError(this.errorHandler.handle()));

        // Observar que para o erro não ser propagado para a página principal, ao usar ignoreErrors() em vez de handle().
        // Neste caso, o método chamador é uma modal que exibe o erro. (Aquino 16-02-2024)
        // return this.http.post<number>(url, formData).pipe(catchError(this.errorHandler.ignoreErrors()));
    }

    downloadAnexoAto(fileName: string, idAto: number, idAnexo: number, tipoConsulta: TipoConsulta) {
        
        const url = this.getUrlAnexoAto(idAto, idAnexo, tipoConsulta);

        this.http.get(url, { responseType: 'blob', observe: 'response' })
            .pipe(catchError(this.errorHandler.handle()))
            .subscribe((response) => {
                const url = URL.createObjectURL(response.body);

                const ancora = document.createElement('a');
                ancora.href = url;
                ancora.download = fileName;
                ancora.click();

                URL.revokeObjectURL(url); // Descarta a URL para o blob
            });
    }

    downloadAnexoDetalhe(id: number, fileName: string) {
        const url = `${this.baseUrlAnexo}/detalhe/${id}`;
        this.http.get(url, { responseType: 'blob', observe: 'response' })
            .pipe(catchError(this.errorHandler.handle()))
            .subscribe((response) => {
                const url = URL.createObjectURL(response.body);

                const ancora = document.createElement('a');
                ancora.href = url;
                ancora.download = fileName;
                ancora.click();

                URL.revokeObjectURL(url); // Descarta a URL para o blob
            });
    }

    buscarPorIdNaVisaoAnotacao(idAto: number): Observable<VisaoAto> {
        const url = `${this.baseUrl}/${idAto}/${VISAO_ANOTACAO.textoParaUrl}`;
        return this.http.get<Ato>(url).pipe(catchError(this.errorHandler.handle()));
    }

    buscarPorIdNaVisao(idAto: number, visao: Visao, tipoConsulta: TipoConsulta): Observable<VisaoAto> {
        const url = this.getBaseUrlConsultas(tipoConsulta) + `/${idAto}/visao/${visao.textoParaUrl}`;
        return this.http.get<Ato>(url).pipe(catchError(this.errorHandler.handle()));
    }

    buscarPorIdNaData(idAto: number, data: string, tipoConsulta: TipoConsulta): Observable<VisaoAto> {
        const url = this.getBaseUrlConsultas(tipoConsulta) + `/${idAto}/data/${data}`;
        return this.http.get<Ato>(url).pipe(catchError(this.errorHandler.handle()));
    }

    buscarVisaoRelacionalDoAto(idAto: number, tipoConsulta: TipoConsulta): Observable<VisaoRelacional> {
        const url = this.getBaseUrlConsultas(tipoConsulta) + `/${idAto}/visao-relacional`;
        return this.http.get<VisaoRelacional>(url).pipe(catchError(this.errorHandler.handle()));
    }

    obterConteudoParaComparacaoPorId(idAto: number): Observable<ComparacaoAto> {
        const url = `${this.baseUrl}/${idAto}/conteudo`;
        return this.http.get<ComparacaoAto>(url).pipe(catchError(this.errorHandler.handle()));
    }

    private getBaseUrlConsultas(tipoConsulta: TipoConsulta): string {
        if (tipoConsulta.isConsultaExterna()) {
            return `${this.baseUrlConsultaExterna}`;
        }
        if (tipoConsulta.isConsultaInterna()) {
            return `${this.baseUrlConsultaInterna}`;
        }
        return `${this.baseUrl}`;
    }

    private getUrlAnexoAto(idAto: number, idAnexo: number, tipoConsulta: TipoConsulta): string {

        if (tipoConsulta.isConsultaExterna()) {
            return `${this.baseUrlConsultaExterna}/${idAto}/anexo/${idAnexo}`;
        }
        if (tipoConsulta.isConsultaInterna()) {
            return `${this.baseUrlConsultaInterna}/${idAto}/anexo/${idAnexo}`;
        }

        return `${this.baseUrlAnexo}/${idAnexo}`;
    }
}
